import { Band } from '../components/editorial/Band';
import { Button } from '../components/Button';
import { Eyebrow } from '../components/Eyebrow';

/** Where a visitor who landed on a dead URL can pick the thread back up. */
const RECOVERY_LINKS = [
  { href: '/catalog', label: 'API catalog' },
  { href: '/docs', label: 'Documentation' },
  { href: '/blog', label: 'Blog' },
  { href: '/sitemap.xml', label: 'Sitemap' },
  { href: '/llms.txt', label: 'llms.txt' },
];

/**
 * Catch-all 404 page for unknown routes.
 *
 * One centred band inside the page's two hairlines, so a dead URL still looks
 * like the site rather than like an error screen. The header is written out
 * here instead of using `PageHeader` because this is the one page whose title
 * stays at `display-1`: it is the only thing on it.
 *
 * The recovery links are the visible half of an agent-readable 404: a client
 * asking for `text/markdown` gets `lib/markdown/notFoundMarkdown.ts` instead,
 * which names the same entry points. Both exist because a 404 with no way
 * forward makes a crawler guess more URLs rather than go where we point.
 */
export const NotFoundPage = () => (
  <Band className="text-center">
    <div className="band">
      <Eyebrow>404</Eyebrow>
      <h1 className="display-1 mt-2 text-text">Page not found</h1>
      <p className="lede mx-auto mt-4 max-w-[44ch]">
        The page you're looking for doesn't exist or has moved.
      </p>
      <div className="mt-7 flex justify-center">
        <Button as="a" to="/" variant="line">
          Back home
        </Button>
      </div>
      <nav aria-label="Where to look next" className="mt-10">
        <p className="text-14 text-text-3">Where to look next</p>
        <ul className="mt-3 flex flex-wrap justify-center gap-x-5 gap-y-2">
          {RECOVERY_LINKS.map((link) => (
            <li key={link.href}>
              <a className="text-14 text-text-2 hover:text-accent" href={link.href}>
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  </Band>
);
